import { useEffect } from "react";
import styled from "@emotion/styled";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  Chip as MuiChip,
  Divider as MuiDivider,
  LinearProgress as MuiLinearProgress,
  Typography as MuiTypography,
  Pagination as MuiPagination,
  Paper as MuiPaper,
  CircularProgress as MuiCircularProgress,
  TextField as MuiTextField,
  Alert as MuiAlert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { spacing, SpacingProps } from "@mui/system";

import { useTranslation } from "react-i18next";
import { IVendor } from "../../../../types/vendor";
import {
  useLazyGetKVKCompanyProfileQuery,
  useSyncVendorWithKVKMutation,
} from "../../../../redux/slices/kvkSlice";
import { format } from "date-fns";
import KvKLogo from "../../../../icons/KvKLogo";

const Alert = styled(MuiAlert)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Card = styled(MuiCard)(spacing);

const Chip = styled(MuiChip)(spacing);

const Divider = styled(MuiDivider)(spacing);

const LinearProgress = styled(MuiLinearProgress)(spacing);

const Spacer = styled.div(spacing);

const Pagination = styled(MuiPagination)(spacing);

const Paper = styled(MuiPaper)(spacing);

const CircularProgress = styled(MuiCircularProgress)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface TypographyProps extends SpacingProps {
  component?: string;
}
const Typography = styled(MuiTypography)<TypographyProps>(spacing);

interface IVendorKVKProps {
  vendor?: IVendor;
}

function VendorKVK({ vendor }: IVendorKVKProps) {
  const { t } = useTranslation();

  // const [
  //   getCompanyProfile,
  //   {
  //     data: companyProfile,
  //     isLoading: gettingCompanyProfile,
  //     isSuccess,
  //     isError,
  //     error,
  //   },
  // ] = useLazyGetCompanyProfileQuery();

  const [
    syncVendorWithKVK,
    {
      isSuccess: isUpdated,
      isLoading: isUpdating,
      isError: updateError,
      error: updateErrorData,
      data: updatedVendor,
      reset: resetUpdate,
    },
  ] = useSyncVendorWithKVKMutation();

  const [
    getCompanyProfile,
    { data: vendorFromKVK, isLoading: gettingCompanyProfile, isSuccess },
  ] = useLazyGetKVKCompanyProfileQuery(vendor?.kvkNumber || "", {
    skip: !vendor?.kvkNumber,
  });

  // useEffect(() => {
  //   // catch the errors
  //   if (isError) {
  //     props.addError(error);
  //   }
  // }, [isError]);

  useEffect(() => {
    resetUpdate();
  }, [vendor?.id]);

  const handleRefreshKVK = async () => {
    if (vendor?.id) {
      syncVendorWithKVK(vendor);
    } else {
      getCompanyProfile(vendor?.kvkNumber || "");
    }
  };

  return (
    <Box flex="1" display="flex" flexDirection="column">
      <Box width="100%">
        <Grid container justifyContent="space-between" direction="row">
          <Grid>
            <Typography variant="h6" mb={2}>
              {t("Company profile")}
            </Typography>
          </Grid>

          <Grid>
            <KvKLogo sx={{ width: 220, marginRight: 2 }} />
          </Grid>
        </Grid>

        <Grid container spacing={2} direction="column">
          <Grid>{JSON.stringify(vendorFromKVK, null, 2)}</Grid>
          <Grid>
            <Typography>
              {t("Registration number")}: {vendor?.kvkNumber}
            </Typography>

            <Typography>
              {t("Founded")}:{" "}
              {vendor?.foundedDate
                ? format(new Date(vendor?.foundedDate), "yyyy")
                : ""}
            </Typography>
          </Grid>

          {updateError && (
            <Grid>
              <Alert severity="error">{updateErrorData?.data?.message}</Alert>
            </Grid>
          )}

          {isUpdated && (
            <Grid>
              <Alert severity="success">{t("Success")}</Alert>
            </Grid>
          )}

          <Grid>
            <Button
              variant="contained"
              onClick={() => handleRefreshKVK()}
              loading={isUpdating}
              disabled={vendor?.kvkNumber === "" || isUpdating}
              // startIcon={<KvKIcon />}
            >
              {t("Retrieve company profile")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default VendorKVK;
